import { useEffect, useState } from "react";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa6";
import { Link, useParams, useNavigate } from "react-router-dom";
import apiClient from "../../../api-services/ApiClient";
import Preloader from "../../../app-components/preloader/Preloader";
import SafeHtmlComponent from "../../../app-components/safehtml/SafeHtmlComponent";
import {
  DefectCriteriaInterface,
  PrioritiesInterface,
  SegmentInterface,
  SegmentQuestionInterface,
} from "../../../interface-and-types/inspection.schema";
import { toast, ToastContainer } from "react-toastify";
import { LuArrowLeft, LuX } from "react-icons/lu";
import { SITELINKS } from "../../../app-routes/links";
import { notifyError } from "../../../app-components/toast/toast";
import { useGlobalState } from "../../../context/GlobalStateContext";

const InServiceAssessmentForm = () => {
  const navigate = useNavigate();
  const { state } = useGlobalState();
  const { id: inspectionFormId } = useParams();
  const [loading, setLoading] = useState(false);
  const [vesselName, setVesselName] = useState(null);
  const [currentSegment, setCurrentSegment] = useState<number>(22);
  const [priorities, setPriorities] = useState<PrioritiesInterface[]>([]);
  const [defectType, setDefectType] = useState<any[]>([]);
  const [vesselStatus, setVesselStaus] = useState(null);
  const [comment, setComment] = useState("");

  const [segment, setSegment] = useState<SegmentInterface[]>([]);

  const [criticalEquipmentTypes, setCriticalEquipmentTypes] = useState<any[]>(
    []
  );

  const [defectFormRows, setDefectFormRows] = useState<
    DefectCriteriaInterface[]
  >([]);

  const [equipmentList, setEquipmentList] = useState<any[]>([]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [approvalID, setApprovalID] = useState("");

  useEffect(() => {
    getInspectionById();
    getPriorities();
    getSegment();
    getCriticalEquipmentTypes();
    getDefectType();
    getEquipmentList();

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (currentSegment !== null) {
      getInspectionFormSegmentDefect(segment);
    }
    // eslint-disable-next-line
  }, [currentSegment]);

  const handleInputChange = <T extends keyof SegmentQuestionInterface>(
    index: number,
    field: T,
    value: SegmentQuestionInterface[T]
  ) => {
    const updatedSegments = [...segment];

    // Get the current segment and ensure it exists
    const currentSegmentInfo = updatedSegments[currentSegment];
    if (!currentSegmentInfo) {
      console.error("Current segment not found.");
      alert("info not accurate");
      return;
    }

    // Ensure segmentQuestions is an array and make a copy of it
    const currentSegmentQuestions = [
      ...(currentSegmentInfo.segmentQuestions || []),
    ];

    // Check if the specified question exists in the array
    const currentQuestionInSegment = currentSegmentQuestions[index];
    if (!currentQuestionInSegment) {
      console.error(`Question at index ${index} not found in segment.`);
      alert("info not accurate");
      return;
    }

    // Update the specific field in the question
    const updatedQuestion = { ...currentQuestionInSegment, [field]: value };

    // Replace the old question with the updated one
    currentSegmentQuestions[index] = updatedQuestion;

    // Update the segment with the modified questions
    updatedSegments[currentSegment] = {
      ...currentSegmentInfo, // Preserve other properties
      segmentQuestions: currentSegmentQuestions,
    };

    // Update the state
    setSegment(updatedSegments);
  };

  // getSegment
  const getInspectionById = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get(
        `InspectionForm/${inspectionFormId}/User`
      );
      setVesselName(res.data?.data?.vessel?.name);
      setVesselStaus(res.data?.data?.formState);

      let approvalID = res.data?.data?.approvalDetail?.approvalId;
      setApprovalID(approvalID);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getSegment
  const getSegment = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Segment");
      const data = res.data.data;
      const sorted = data.sort(
        (a: any, b: any) => a.segmentNumber - b.segmentNumber
      );
      setSegment(sorted);
      await getInspectionFormSegmentDefect(sorted);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // get the user filled details
  const getInspectionFormSegmentDefect = async (segmentResponse: any) => {
    setLoading(true);

    try {
      const segmentInfo = segmentResponse[currentSegment];

      if (!segmentInfo) {
        console.error("Current segment not found.");
        return;
      }

      const segmentId = segmentInfo.id;

      // Fetch inspection form data
      const res: any = await apiClient.get(
        `InspectionForm/${inspectionFormId}/FormSegment/${segmentId}/Section`
      );

      const inspectionFormSegment =
        res.data?.data?.inspectionFormSegments ?? [];

      const segmentQuestions: any = segmentInfo.segmentQuestions || [];

      let updatedSegmentQuestions = [];
      if (segmentQuestions.length > 0) {
        updatedSegmentQuestions = segmentQuestions.map((item: any) => {
          const matchingSegment = inspectionFormSegment.find(
            (seg: any) => seg.questionNumber === item.questionNumber
          );

          return {
            ...item,
            answer: matchingSegment?.suitabilityStatus || "", // Use fallback if not found
            comment: matchingSegment?.commentsOrFinds || null,
            priority: matchingSegment?.priority || null,
          };
        });
      } else {
        updatedSegmentQuestions = inspectionFormSegment.map((item: any) => {
          return {
            ...item,
            answer: item?.suitabilityStatus || "", // Use fallback if not found
            comment: item?.commentsOrFinds || null,
            priority: item?.priority || null,
          };
        });
      }

      segmentInfo.segmentQuestions = updatedSegmentQuestions;
      segmentResponse[currentSegment] = segmentInfo;

      const vesselDefectCheckLists =
        res.data?.data?.vesselDefectCheckLists ?? [];
      console.log(vesselDefectCheckLists);

      let updatedVesselDefectCheckLists = [];
      if (vesselDefectCheckLists.length > 0) {
        updatedVesselDefectCheckLists = vesselDefectCheckLists.map(
          (item: any) => {
            return {
              ...item,
              equipmentName: item?.equipmentName || "",
              equipmentId: item?.equipment.id || "",
              criticalEquipment:
                item?.criticalEquipment === "Yes"
                  ? "1"
                  : item?.criticalEquipment === "No"
                  ? "2"
                  : null,
              defectTypeId: item?.defectTypeId || null,
              comments: item?.comments || null,
              priority: item?.priorityId || null,
              defectCheckListAttachmentsList:
                item?.defectCheckListAttachments || [],
            };
          }
        );
      }

      setDefectFormRows(updatedVesselDefectCheckLists);

      setSegment(segmentResponse);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getPriorities
  const getPriorities = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/Priorities");
      setPriorities(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getCriticalEquipmentTypes
  const getCriticalEquipmentTypes = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Constants/CriticalEquipmentTypes");
      setCriticalEquipmentTypes(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  // getCriticalEquipmentTypes
  const getEquipmentList = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("Equipment");
      setEquipmentList(res.data.data);
    } catch (error) {
      console.error("Error fetching", error);
    } finally {
      setLoading(false);
    }
  };

  // getDefectType
  const getDefectType = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.get("DefectType");
      setDefectType(res.data.data);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
    } finally {
      setLoading(false);
    }
  };

  const prevPage = async () => {
    await setCurrentSegment(currentSegment - 1);
  };

  const nextPage = async () => {
    await setCurrentSegment(currentSegment + 1);
  };

  const sectionDropdown = async (page: any) => {
    await setCurrentSegment(page);
  };

  const downloadFile = async () => {
    try {
      setLoading(true);
      await apiClient
        .get(`InspectionForm/${inspectionFormId}/Download`)
        .then((res) => {
          console.log(res);
          var a = document.createElement("a");
          a.href = "data:image/png;base64," + res.data.data.base64String;
          a.download = res.data.data.fileName; // res.data.data.attachmentType.name+'-'+res.data.data.name
          a.click();

          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          setLoading(false);
        });
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (status: string) => {
    setLoading(true);
    try {
      const data = {
        approvalStatus: status,
        comment: comment,
      };

      await apiClient.patch(`InspectionApproval/${approvalID}`, data);

      toast.success("Success", {
        autoClose: 60000,
        theme: "colored",
      });

      navigate(SITELINKS.INSPECTION_APPROVAl_HISTORY);
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);

      notifyError(JSON.stringify(error));
    } finally {
      setLoading(false);
      setModalOpen(false);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-4 pb-24 flex justify-between items-center">
          <h4 className="lg:text-[16px] text-white nunito font-medium flex gap-2 items-center">
            <Link to={SITELINKS.INSPECTION_HISTORY} className="text-2xl">
              <LuArrowLeft />
            </Link>
            <div>
              In-Service Form /{" "}
              <span className="text-slate-100">{vesselName}</span>
            </div>
          </h4>

          <select
            id="array-length-dropdown"
            className="text-md border-b bg-transparent text-gray-100 hidden"
            value={currentSegment}
            onChange={(e) => sectionDropdown(Number(e.target.value))}
          >
            {Array.from({ length: segment.length }, (_, index) => (
              <option key={index} value={index} className="bg-slate-700">
                Section {index + 1}
              </option>
            ))}
          </select>

          <div className="flex gap-1">
            <button
              className="disabled:bg-gray-400 flex gap-2 items-center justify-start text-xs bg-slate-500 shadow-sm p-2 rounded text-white font-medium px-2 hidden"
              type="button"
              onClick={prevPage}
              disabled={currentSegment === 0}
            >
              <FaChevronLeft className="text-md" />
              <span>Back</span>
            </button>

            <button
              className="disabled:bg-gray-600 flex gap-2 items-center text-xs bg-slate-500 shadow p-2 rounded text-white font-medium px-2 hidden"
              type="button"
              onClick={nextPage}
              disabled={segment.length - 1 === currentSegment}
            >
              <span>Next</span>
              <FaChevronRight className="text-md" />
            </button>

            {vesselStatus === "Completed" && (
              <button
                className="flex gap-1 items-center text-xs bg-green-600 shadow p-2 rounded text-white font-medium px-2"
                type="button"
                onClick={downloadFile}
              >
                <span>FInal PDF</span>
              </button>
            )}

            {state.tepnguser.roles.find(
              (item: any) => item === "MarineApprover"
            ) &&
              vesselStatus === "PendingApproval" && (
                <button
                  className="flex gap-1 items-center text-xs bg-blue-600 shadow p-2 rounded text-white font-medium px-2"
                  type="button"
                  onClick={() => setModalOpen(true)}
                >
                  <span>Action Inspection</span>
                </button>
              )}
          </div>
        </div>
      </section>

      {/* page content */}
      <section className="pt-2 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-90px]">
        <div className="rounded-xl bg-white border border-gray-200 mb-5 hidden">
          <div className="p-4 2xl:p-6 pb-8">
            <div className="flex justify-between text-slate-800 items-center mb-4 font-medium">
              <h1 className="font-medium text-sm text-gray-800">
                {segment[currentSegment]?.name}{" "}
                <small className="text-red-500">
                  (*) All fields are required!
                </small>
              </h1>

              <span className="bg-gray-100  text-sm rounded-full px-2">
                {currentSegment + 1} of {segment?.length}
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="w-full bg-gray-200 text-gray-600 capitalize text-xs leading-normal text-left">
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      SN
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      {segment[currentSegment]?.name}
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      Yes
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      No
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      N/A
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      Comments
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800">
                      Priority
                    </th>
                  </tr>
                </thead>

                <tbody className="text-gray-600 text-xs font-normal">
                  {segment[currentSegment]?.segmentQuestions.map(
                    (row, index) => (
                      <tr key={index} className="even:bg-gray-50">
                        <td className="border border-gray-300 px-2 py-1 text-center">
                          {index + 1}
                        </td>

                        <td className="border border-gray-300 px-2 py-1">
                          <SafeHtmlComponent html={row.question} />
                        </td>

                        <td className="border border-gray-300 px-2 py-1 text-center">
                          <input
                            className="h-[1.5rem] w-[1.5rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            disabled
                            checked={
                              row?.answer?.toUpperCase() === "YES" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "YES")
                            }
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-1 text-center">
                          <input
                            className="h-[1.5rem] w-[1.5rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            disabled
                            checked={
                              row?.answer?.toUpperCase() === "NO" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "NO")
                            }
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-1 text-center">
                          <input
                            className="h-[1.5rem] w-[1.5rem] rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300"
                            type="checkbox"
                            disabled
                            checked={
                              row?.answer?.toUpperCase() === "NA" || false
                            }
                            onChange={(e) =>
                              handleInputChange(index, "answer", "NA")
                            }
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-1">
                          <input
                            type="text"
                            className="min-w-[300px] w-full border p-1.5 rounded"
                            value={row?.comment || ""}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "comment",
                                e.target.value
                              )
                            }
                            placeholder="Add comments"
                            disabled
                          />
                        </td>

                        <td className="border border-gray-300 px-2 py-1">
                          <select
                            className="w-full border py-1.5 hover:border-slate-500 rounded p-1 w-[100px]"
                            value={row?.priority || ""}
                            disabled
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "priority",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select</option>
                            {priorities.map((priority, i) => {
                              return (
                                <option key={i} value={priority?.value}>
                                  {priority?.name}
                                </option>
                              );
                            })}
                          </select>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Defect form criteria */}
        <div className="rounded-lg bg-white border border-gray-200 mb-10">
          <div className="p-4">
            <div className="flex justify-between text-slate-800 items-center mb-4 font-medium">
              <h1 className="text-sm">Defect Form Criteria</h1>
            </div>

            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-200 text-left text-xs">
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      SN
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      Equipment Name
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800  align-top">
                      Critical Equipment
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Type of Defect
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Comments
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Priority
                    </th>
                    <th className="border border-gray-300 px-2 py-2 font-medium text-slate-800 align-top">
                      Attach Picture
                    </th>
                  </tr>
                </thead>

                <tbody className="text-xs">
                  {defectFormRows.map((row, index) => (
                    <tr key={index} className="even:bg-gray-50">
                      <td className="border border-gray-300 px-2 py-1 align-top">
                        {index + 1}
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <span>
                          {
                            equipmentList.find(
                              (item: any) => item.id === row?.equipmentId
                            )?.name
                          }
                        </span>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <span>
                          {
                            criticalEquipmentTypes.find(
                              (item: any) =>
                                Number(item.id) ===
                                Number(row?.criticalEquipment)
                            )?.name
                          }
                        </span>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <span>
                          {
                            defectType.find(
                              (item: any) => item.id === row?.defectTypeId
                            )?.name
                          }
                        </span>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <span>{row?.comments}</span>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 align-top">
                        <span>
                          {
                            priorities.find(
                              (item: any) => item.id === row?.priority
                            )?.name
                          }
                        </span>
                      </td>

                      <td className="border border-gray-300 px-2 py-1 w-[160px] align-top">
                        <div className="flex gap-2 flex-grow mt-1">
                          {row?.defectCheckListAttachments.map(
                            (item: any, index: number) => (
                              <img
                                alt="AttchedItem"
                                src={`data:image/png;base64,${item.base64Image}`}
                                key={index}
                                className="pb-1 h-[20px] bg-slate-50"
                              />
                            )
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {defectFormRows.length === 0 && (
                <div className="p-10 text-center text-slate-600 text-xs border">
                  No record
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* add new questions */}
      {isModalOpen && (
        <section className="fixed inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center">
          <button
            onClick={() => setModalOpen(false)}
            className="mt-4 text-red-600 hover:underline p-3 rounded-full bg-white mb-4 text-xl border border-gray-500 shadow animate-modalShow"
          >
            <LuX />
          </button>

          <div className="bg-white p-12 rounded shadow-md w-full max-w-xl animate-modalShow">
            <form autoComplete="off">
              <h2 className="text-md font-medium mb-4 flex-col border-b">
                Approve Form
              </h2>

              <div className="max-h-[55vh]">
                <div className="overflow-x-auto">
                  <textarea
                    className="w-full border border-1 p-2"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Enter comment"
                  ></textarea>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => handleApproval("Approved")}
                  type="button"
                  className="w-full mx-auto flex font-normal justify-center mt-12 text-sm bg-green-700 text-white py-2 rounded hover:bg-green-600"
                >
                  Approve
                </button>
                <button
                  onClick={() => handleApproval("Reject")}
                  type="button"
                  className="w-full mx-auto flex font-normal justify-center mt-12 text-sm bg-red-700 text-white py-2 rounded hover:bg-red-600"
                >
                  Decline
                </button>
              </div>
            </form>
          </div>
        </section>
      )}

      <ToastContainer position="bottom-left" hideProgressBar />
      <Preloader loading={loading} />
    </>
  );
};

export default InServiceAssessmentForm;
